import { formatDateForAPI } from '@internal/backstage-plugin-common-insights-components-react';
import { useTeamTree } from '@internal/backstage-plugin-common-library-react';
import { useMemo } from 'react';
import { useAsync } from 'react-use';

import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';

import { ScorecardItem } from '../../types';

const secondsToHours = (seconds: number): string => {
  return `${(seconds / 3600).toFixed(2)}h`;
};

const secondsToMinutes = (seconds: number): string => {
  return `${(seconds / 60).toFixed(2)}m`;
};

const secondsToDays = (seconds: number): string => {
  const days = (seconds / 86400).toFixed(2);
  return `${days}d`;
};

const locTransform = (value: number) => {
  return `${value}LoC`;
};

const toStartOfDay = (date: string | Date): string => {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  return formatDateForAPI(d);
};

const toEndOfDay = (date: string | Date): string => {
  const d = new Date(date);
  d.setUTCHours(23, 59, 59, 0);
  return formatDateForAPI(d);
};

const metrics = [
  // DORA
  {
    label: 'P75 Lead Time for Changes',
    path: 'pr/lead-time',
    transform: secondsToDays,
    percentile: 0.75,
  },
  {
    label: 'Avg Lead Time for Changes',
    path: 'pr/lead-time',
    transform: secondsToDays,
  },

  // Time to Pickup
  {
    label: 'P75 Time to Pickup',
    path: 'pr/pickup-time',
    transform: secondsToHours,
    percentile: 0.75,
  },
  {
    label: 'Avg Time to Pickup',
    path: 'pr/pickup-time',
    transform: secondsToHours,
  },

  // Time to Review
  {
    label: 'P75 Time to Review',
    path: 'pr/review-time',
    transform: secondsToHours,
    percentile: 0.75,
  },
  {
    label: 'Avg Time to Review',
    path: 'pr/review-time',
    transform: secondsToHours,
  },

  // Time to Merge
  {
    label: 'P75 Time to Merge',
    path: 'pr/merge-time',
    transform: secondsToHours,
    percentile: 0.75,
  },
  {
    label: 'Avg Time to Merge',
    path: 'pr/merge-time',
    transform: secondsToHours,
  },

  // CI/CD duration
  {
    label: 'Avg CI/CD duration',
    path: 'ci/build-duration',
    transform: secondsToMinutes,
  },
  // CI/CD Success Rate
  {
    label: 'CI/CD Success Rate',
    path: 'ci/success-rate',
    transform: (value: number) => `${(value * 100).toFixed(2)}%`,
  },
  // PR Size
  {
    label: 'P75 PR Size',
    path: 'pr/pr-size',
    transform: locTransform,
    percentile: 0.75,
  },
  { label: 'Avg PR Size', path: 'pr/pr-size', transform: locTransform },
];

export const useGetEngInsightsItems = (
  rootEntity: string,
  startDate: string,
  endDate: string,
  orgWide: boolean,
  ownerConfirmed: boolean = true,
  productionEnvironment: boolean = true,
): { value: ScorecardItem[]; loading: boolean } => {
  const entities = useTeamTree(rootEntity)?.teamTree?.flatTree;

  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const normalizedStartDate = useMemo(
    () => toStartOfDay(startDate),
    [startDate],
  );
  const normalizedEndDate = useMemo(() => toEndOfDay(endDate), [endDate]);

  const payload = useMemo(
    () => ({
      start_date: normalizedStartDate,
      end_date: normalizedEndDate,
      aggregate_by: 'month',
      production_environment: productionEnvironment,
      owner_confirmed: ownerConfirmed,
      isTeamLevel: !orgWide,
      ...(!orgWide && { team_ids: entities }),
    }),
    [
      normalizedStartDate,
      normalizedEndDate,
      orgWide,
      entities,
      ownerConfirmed,
      productionEnvironment,
    ],
  );

  const { value: metricResponse, loading } = useAsync(async () => {
    try {
      const { token } = await identityApi.getCredentials();
      const endpointBase = await discoveryApi.getBaseUrl('watchtower');

      const promises = metrics.map(async metric => {
        const response = await fetch(`${endpointBase}/metric`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...payload,
            metric: metric.path,
            ...(metric.percentile !== undefined && {
              percentile: metric.percentile,
            }),
          }),
        });
        const data = await response.json();
        return { data, label: metric.label, transform: metric.transform };
      });

      const results = await Promise.allSettled(promises);

      return results
        .filter(
          (
            result,
          ): result is PromiseFulfilledResult<{
            data: any;
            label: string;
            transform: (value: number) => string;
          }> => result.status === 'fulfilled' && result.value?.data?.periods,
        )
        .map(result => ({
          label: result.value.label,
          valueOverTime: result.value.data.periods.map(
            (p: { start: string; value: number }) => ({
              date: p.start,
              value: result.value.transform(p.value),
            }),
          ),
        })) as ScorecardItem[];
    } catch (e) {
      return null;
    }
  }, [normalizedStartDate, normalizedEndDate, payload]);

  return { value: metricResponse ?? [], loading };
};
